import Typography from "#/components/shared/typography";

type TextSpan = { text?: string };
type ContentSection = { id: string; title?: string; order?: number };
type ContentBlock = {
  id: string;
  sectionId?: string | null;
  order_index?: number;
  type: string;
  level?: number;
  text_spans?: TextSpan[];
  data?: { ordered?: boolean } & Record<string, unknown>;
};
type ContentAsset = {
  id: string;
  blockId?: string | null;
  sectionId?: string | null;
  url?: string | null;
  asset?: { url?: string | null; meta?: Record<string, unknown> | null } | null;
  placement?:
    | ({ assetUrl?: string | null; placement?: string; flow?: string } &
        Record<string, unknown>)
    | null;
};

function textFromSpans(spans?: TextSpan[]) {
  return (spans || []).map((s) => s.text || "").join("").trim();
}

function assetUrl(asset?: ContentAsset) {
  return asset?.placement?.assetUrl || asset?.asset?.url || asset?.url || "";
}

function wantsInlineLeftImage(asset?: ContentAsset) {
  const placement = String(asset?.placement?.placement || "").toLowerCase();
  const flow = String(asset?.placement?.flow || "").toLowerCase();
  return placement === "left" || flow === "side-by-side";
}

export default function StructuredArticleRenderer({
  sections,
  blocks,
  assets,
}: {
  sections: ContentSection[];
  blocks: ContentBlock[];
  assets: ContentAsset[];
}) {
  const orderedSections = [...(sections || [])].sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0)
  );

  const sectionList =
    orderedSections.length > 0
      ? orderedSections
      : [{ id: "__default_section__", title: undefined, order: 0 }];

  const assetsByBlock = new Map<string, ContentAsset[]>();
  (assets || []).forEach((a) => {
    if (!a.blockId) return;
    const list = assetsByBlock.get(a.blockId) || [];
    list.push(a);
    assetsByBlock.set(a.blockId, list);
  });

  return (
    <div className="max-w-4xl mx-auto rounded-md bg-cream-100 p-6">
      <div className="space-y-6">
        {sectionList.map((section) => {
          const sectionBlocks = (blocks || [])
            .filter((b) =>
              section.id === "__default_section__"
                ? true
                : (b.sectionId || "") === section.id
            )
            .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0));

          return (
            <section key={section.id} className="space-y-3">
              {section.title ? (
                <Typography variant="h4" className="text-gray-900">
                  {section.title}
                </Typography>
              ) : null}

              {sectionBlocks.map((block, index) => {
                if (block.type === "list") {
                  const items: ContentBlock[] = [];
                  let cursor = index + 1;
                  while (
                    cursor < sectionBlocks.length &&
                    sectionBlocks[cursor]?.type === "list_item"
                  ) {
                    items.push(sectionBlocks[cursor]);
                    cursor += 1;
                  }

                  if (!items.length) return null;

                  const ordered = Boolean(block.data?.ordered);
                  const ListTag = ordered ? "ol" : "ul";

                  return (
                    <ListTag
                      key={block.id}
                      className={ordered ? "list-decimal pl-6" : "list-disc pl-6"}
                    >
                      {items.map((item) => {
                        const itemAssets = assetsByBlock.get(item.id) || [];
                        const inlineAsset = itemAssets.find(
                          (a) => wantsInlineLeftImage(a) && assetUrl(a)
                        );

                        if (inlineAsset) {
                          return (
                            <li key={item.id} className="mb-2 text-gray-700">
                              <div className="grid grid-cols-[72px_1fr] items-start gap-3">
                                <img
                                  src={assetUrl(inlineAsset)}
                                  alt=""
                                  className="h-[72px] w-[72px] rounded-md border border-beige-200 object-cover"
                                />
                                <div>{textFromSpans(item.text_spans)}</div>
                              </div>
                            </li>
                          );
                        }

                        return (
                          <li key={item.id} className="mb-1 text-gray-700">
                            {textFromSpans(item.text_spans)}
                          </li>
                        );
                      })}
                    </ListTag>
                  );
                }

                if (block.type === "list_item") return null;

                const blockText = textFromSpans(block.text_spans);
                const blockAssets = assetsByBlock.get(block.id) || [];

                return (
                  <div key={block.id} className="space-y-2">
                    {block.type === "heading" ? (
                      <Typography
                        variant={(block.level || 2) <= 2 ? "h4" : "h5"}
                        className="text-gray-900"
                      >
                        {blockText}
                      </Typography>
                    ) : block.type === "callout" ? (
                      <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                        {blockText}
                      </div>
                    ) : (
                      <Typography variant="body1" className="text-gray-700">
                        {blockText}
                      </Typography>
                    )}

                    {blockAssets.map((a) => {
                      const url = assetUrl(a);
                      if (!url) return null;
                      return (
                        <img
                          key={a.id}
                          src={url}
                          alt=""
                          className="w-full rounded-md border border-beige-200"
                        />
                      );
                    })}
                  </div>
                );
              })}
            </section>
          );
        })}
      </div>
    </div>
  );
}
